import { getCurrentMatchesGradient, getTextColor } from "@/helper/color";
import { getDate, getDayOfWeek, getMatchTimeLabel } from "@/helper/time";
import { useRouter } from "next/router";

// import { GamescheduleMatchList } from "@/components/Gameschedule/GamescheduleMatchList";
import { NewsCard } from "@/components/News/NewsCard";
import { DetailedGame, DetailedMatch, type Card } from "@/data/match";
import { BasicLeaguePlayer } from "@/helper/match";
import {
  getPlayerIdsBySide,
  getPointsOnServeByGames,
  getServerFromScoringUpdates,
} from "@/helper/matchStats";
import { Sets, scoreOrDash, winnerByScore } from "@/helper/scoresHelper";
import { getGameLinupNumber, useBreakpoint } from "@/helper/util";
import { Season } from "@/models/Season";
import { News } from "@/models/types";
import type { MatchState, ScoringUpdates } from "@prisma/client";
import { isArray } from "lodash";
import { useTranslation } from "next-i18next";
import { useEffect, useState } from "react";
import {
  FiBarChart2,
  FiCalendar,
  FiChevronRight,
  FiClock,
  FiMapPin,
  FiMinus,
  FiUser,
} from "react-icons/fi";
import Ticket from "../../assets/ticket.svg";
import { MatchWithTeamsAndVenueAndGamedayAndSeason } from "../../models/Match";
import { LinkButton } from "../common/Button";
import { PlayerImage, TeamImage, VenueImage } from "../common/Images";
import { ResponsiveContainer } from "../common/ResponsiveContainer";
import Image from "next/image";

interface GamedayMatchDetailProps {
  match: DetailedMatch;
  news?: News | null;
  season: Season;
  comparisonMatches: MatchWithTeamsAndVenueAndGamedayAndSeason[];
}

export function GamedayMatchDetail({
  match,
  news,
  season,
  // comparisonMatches,
}: GamedayMatchDetailProps) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col">
      {match.ticketshopUrl && (
        <div className="container container-padding-responsive bg-white p-4 flex flex-col space-y-2">
          <LinkButton
            href={match.ticketshopUrl}
            iconLeft={Ticket}
            color="yellow"
            fullWidth
          >
            {t("Buy_Tickets")}
          </LinkButton>
        </div>
      )}
      {match.games.length > 0 && <GamesView match={match} />}
      <div className="container container-padding-responsive">
        <ResponsiveContainer order={["order-2", "order-1"]}>
          <div title={t("Team_Comparison") as string}>
            <TeamComparisonCard match={match} season={season} />
          </div>
          <div title={t("Match_Statistics") as string}>
            <MatchStatCard match={match} />
          </div>
        </ResponsiveContainer>
      </div>
      <div className="container container-padding-responsive">
        <ResponsiveContainer>
          <div title={t("Game_Info") as string}>
            <MatchVenue match={match} />
          </div>
          <div>
            {news && (
              <div title={t("Match_Report") as string} className="p-4 lg:p-16">
                <NewsCard news={news} />
              </div>
            )}
            {/*
                TODO: Add back in when we have a way to get the comparison matches.
            <div title="Letzte direkte Vergleiche">
              <GamescheduleMatchList matches={comparisonMatches} />
            </div> */}
          </div>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

interface TeamComparisonCardProps {
  match: DetailedMatch;
  season: Season;
}
function TeamComparisonCard({ match, season }: TeamComparisonCardProps) {
  const { t } = useTranslation();

  return (
    <div className="bg-white p-4">
      <div className="flex justify-between">
        <TeamImage team={match.homeTeam} />
        <div>{`${t("Label_Season")} ${season.startYear}/${
          season.endYear
        }`}</div>
        <TeamImage team={match.awayTeam} />
      </div>
      <StatCard
        label={t("Label_Rank")}
        homeValue={match.homeTeam?.rank}
        awayValue={match.awayTeam?.rank}
      />
      <StatCard
        label={t("Label_Points")}
        homeValue={match.homeTeam?.plusPoints}
        awayValue={match.awayTeam?.plusPoints}
      />
      <StatCard
        label={t("Label_Games")}
        homeValue={match.homeTeam?.gameWins}
        awayValue={match.awayTeam?.gameWins}
      />
      <StatCard
        label={t("Sets")}
        homeValue={match.homeTeam?.setWins}
        awayValue={match.awayTeam?.setWins}
      />
    </div>
  );
}
interface MatchStatCardProps {
  match: DetailedMatch;
}
function MatchStatCard({ match }: MatchStatCardProps) {
  const homeLuckyPoints = match.games.reduce(
    (acc, g) => acc + g.homeLuckyPoints,
    0,
  );
  const awayLuckyPoints = match.games.reduce(
    (acc, g) => acc + g.awayLuckyPoints,
    0,
  );
  const { t } = useTranslation();
  const {
    awayPointsOnReturn,
    awayPointsOnServe,
    homePointsOnReturn,
    homePointsOnServe,
  } = getPointsOnServeByGames(match.games);
  return (
    <div className="bg-white p-4">
      <StatCard
        label={t("Label_Games")}
        homeValue={match.homeGames}
        awayValue={match.awayGames}
      />
      <StatCard
        label={t("Sets")}
        homeValue={match.homeSets}
        awayValue={match.awaySets}
      />
      <StatCard
        label={t("Label_Points")}
        homeValue={match.homeBalls}
        awayValue={match.awayBalls}
      />
      <StatCard
        label={t("Points_On_Serve")}
        homeValue={homePointsOnServe}
        awayValue={awayPointsOnServe}
      />
      <StatCard
        label={t("Points_On_Return")}
        homeValue={homePointsOnReturn}
        awayValue={awayPointsOnReturn}
      />
      <StatCard
        label="Lucky Points"
        homeValue={homeLuckyPoints}
        awayValue={awayLuckyPoints}
      />
    </div>
  );
}

interface GamesCardProps {
  match: DetailedMatch;
}
function GamesView({ match }: GamesCardProps) {
  const firstSelectedGame =
    match.games.find((g) => g.gameState === "Active") ?? match.games[0];
  const [selectedGame, setSelectedGame] = useState(firstSelectedGame);
  const { t } = useTranslation();

  const isMobile = useBreakpoint();
  useEffect(() => {
    setSelectedGame(firstSelectedGame);
  }, [firstSelectedGame]);

  function setGame(game: DetailedGame) {
    setSelectedGame(game);
    if (isMobile) {
      window.scrollTo({ behavior: "smooth", top: 200, left: 0 });
    }
  }

  return (
    <div className="container container-padding-responsive flex flex-col ">
      <ResponsiveContainer
        order={["order-2 lg:order-none", "order-1 lg:order-none"]}
      >
        <div>
          <div title={t("Label_Games") as string}>
            <GamesList
              match={match}
              games={match.games}
              selectedGame={selectedGame}
              setSelectedGame={setGame}
            />
          </div>
          <div title={t("Lineup") as string}>
            <Lineup match={match} />
          </div>
        </div>
        <div title={t("Game_Detail") as string}>
          <GameDetail game={selectedGame} match={match} />
        </div>
      </ResponsiveContainer>
    </div>
  );
}

interface GamesListProps {
  games: DetailedGame[];
  setSelectedGame: (game: DetailedGame) => void;
  selectedGame: DetailedGame;
  match: DetailedMatch;
}
function GamesList({
  match,
  games,
  selectedGame,
  setSelectedGame,
}: GamesListProps) {
  return (
    <div>
      {games.map((game) => (
        <GamesListCard
          match={match}
          game={game}
          key={game.index}
          isSelected={selectedGame.index === game.index}
          setSelectedGame={setSelectedGame}
        />
      ))}
    </div>
  );
}

interface GamesListCardProps {
  game: DetailedGame;
  setSelectedGame: (game: DetailedGame) => void;
  isSelected: boolean;
  match: DetailedMatch;
}
function GamesListCard({
  match,
  game,
  isSelected,
  setSelectedGame,
}: GamesListCardProps) {
  return (
    <button
      className={`w-full flex items-center p-3 md:rounded-md md:shadow md:border-2 md-1 md:mb-4 last:mb-0 bg-white border-b-gray-light border
      ${isSelected ? `md:border-black ` : "cursor-pointer"}`}
      onClick={(e) => {
        e.preventDefault();
        setSelectedGame(game);
      }}
    >
      <FiChevronRight />
      <div className="flex flex-col space-y-3 border-l border-gray-light ml-3 pl-3 flex-1">
        <div>
          <GameListCardPlayer
            match={match}
            player={
              game.homePlayer
                ? game.homePlayer
                : game.homeDouble
                  ? [
                      game.homeDouble.leaguePlayerOne,
                      game.homeDouble.leaguePlayerTwo,
                    ]
                  : null
            }
            game={game}
            sets={game.homeSets}
            matchState={match.matchState}
            isWinner={
              game.winnerSide != undefined
                ? game.winnerSide === "Home"
                : undefined
            }
          />
        </div>
        <div>
          <GameListCardPlayer
            match={match}
            player={
              game.awayPlayer
                ? game.awayPlayer
                : game.awayDouble
                  ? [
                      game.awayDouble.leaguePlayerOne,
                      game.awayDouble.leaguePlayerTwo,
                    ]
                  : null
            }
            game={game}
            sets={game.awaySets}
            matchState={match.matchState}
            isWinner={
              game.winnerSide != undefined
                ? game.winnerSide === "Away"
                : undefined
            }
          />
        </div>
      </div>
    </button>
  );
}

const GameListCardPlayer = ({
  match,
  player,
  game,
  sets,
  matchState,
  isWinner,
}: {
  match: DetailedMatch;
  player:
    | BasicLeaguePlayer
    | [BasicLeaguePlayer | null, BasicLeaguePlayer | null]
    | null;
  game: DetailedGame;
  sets: number;
  matchState: MatchState;
  isWinner?: boolean;
}) => {
  return (
    <div className="flex items-center">
      <div className="flex items-center space-x-2 flex-1">
        {player && (
          <PlayerImage
            player={isArray(player) ? player[0] : player}
            size="sm"
          />
        )}
        <div
          className={`text-lg font-bold ${getTextColor(matchState, isWinner)}`}
        >
          {isArray(player) ? player[0]?.lastName : player?.lastName}
        </div>
        <div className="text-gray-text">
          {isArray(player)
            ? getGameLinupNumber(match, player[0]?.id)
            : getGameLinupNumber(match, player?.id)}
        </div>
        {isArray(player) && (
          <>
            <PlayerImage player={player[1]} size="sm" />
            <div
              className={`text-lg font-bold ${getTextColor(
                matchState,
                isWinner,
              )}`}
            >
              {player[1]?.lastName}
            </div>
            <div className="text-gray-text">
              {getGameLinupNumber(match, player[1]?.id)}
            </div>
          </>
        )}
      </div>
      <div
        className={`${getTextColor(matchState, isWinner)} text-3xl font-bold`}
      >
        {game.gameState !== "Inactive" ? sets : "-"}
      </div>
    </div>
  );
};

interface GameDetailProps {
  game: DetailedGame;
  match: DetailedMatch;
}
function GameDetail({ game, match }: GameDetailProps) {
  const { t } = useTranslation();

  const currentServer = getServerFromScoringUpdates(
    game as DetailedGame & { scoringUpdates: ScoringUpdates[] },
  );
  const scoringSides = getPlayerIdsBySide(game);
  return (
    <>
      {/* <ErrorBoundary
        fallback={<div>Spiel konnte nicht geladen werden</div>}
        onError={(error, info) => {
          console.log({ error, info });
        }}
      > */}
      <div className="bg-white border-black lg:border-2 rounded-md">
        <div className="grid grid-cols-2 gap-4 p-4">
          <div className="flex flex-col">
            <div className="flex justify-end gap-4 mb-1">
              <div
                className={`w-12 h-12 relative bg-gradient-to-b inline-flex items-center justify-center rounded
            ${getCurrentMatchesGradient(match.timeStamp, match.matchState)}
            `}
              >
                <span className="absolute font-antonio font-bold text-white text-3xl transform -translate-y-0.5">
                  {game.homeSets}
                </span>
              </div>
              {game.homePlayer ? (
                <PlayerImage player={game.homePlayer} size="lg" />
              ) : game.homeDouble ? (
                <>
                  <PlayerImage
                    player={game.homeDouble.leaguePlayerOne}
                    size="lg"
                  />
                  <PlayerImage
                    player={game.homeDouble?.leaguePlayerTwo}
                    size="lg"
                  />
                </>
              ) : null}
            </div>
            <div className="flex justify-between">
              <div className="content-center md:ml-4">
                {currentServer === "Home" && (
                  <Image
                    height={500}
                    width={500}
                    className=" aspect-square w-8"
                    src="/assets/placeholder/Racket.png"
                    alt="Aufschlag Heim"
                  />
                )}
              </div>
              <div>
                <div className=" text-sm text-text-dark text-end">
                  {game.homePlayer ? (
                    game.homePlayer.firstName
                  ) : (
                    <div className="flex gap-2 justify-end text-xl font-bold">
                      <div className="text-gray-text transform translate-y-px font-normal">
                        {getGameLinupNumber(
                          match,
                          game.homeDouble?.leaguePlayerOne?.id,
                        )}
                      </div>
                      {game.homeDouble?.leaguePlayerOne?.lastName}
                    </div>
                  )}
                </div>

                <div className="flex gap-2 justify-end items-center">
                  {game.homePlayer && (
                    <div className="text-gray-text transform translate-y-px">
                      {getGameLinupNumber(match, game.homePlayer.id)}
                    </div>
                  )}
                  <div className="text-xl font-bold text-text-dark">
                    {game.homePlayer ? (
                      game.homePlayer.lastName
                    ) : (
                      <div className="flex gap-2">
                        <div className="text-gray-text transform translate-y-px">
                          {getGameLinupNumber(
                            match,
                            game.homeDouble?.leaguePlayerTwo?.id,
                          )}
                        </div>
                        {game.homeDouble?.leaguePlayerTwo?.lastName}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div
              className={`w-full h-0.5 my-2 bg-gradient-to-r ${getCurrentMatchesGradient(
                match.timeStamp,
                match.matchState,
              )}`}
            />
            <div className="flex space-x-2 items-center justify-center w-full pt-2">
              {Sets.map((i) => (
                <div
                  key={i}
                  className={`
                rounded-full border border-gray-dark w-4 h-4
                ${
                  winnerByScore(game, i) === "Home"
                    ? match.matchState === "Active"
                      ? "bg-orange"
                      : "bg-black"
                    : ""
                }`}
                />
              ))}
            </div>
          </div>

          <div className="flex flex-col">
            <div className="flex justify-start gap-4 mb-1">
              {game.awayPlayer ? (
                <PlayerImage player={game.awayPlayer} size="lg" />
              ) : game.awayDouble ? (
                <>
                  <PlayerImage
                    player={game.awayDouble.leaguePlayerOne}
                    size="lg"
                  />
                  <PlayerImage
                    player={game.awayDouble.leaguePlayerTwo}
                    size="lg"
                  />
                </>
              ) : null}
              <div
                className={`w-12 h-12 relative bg-gradient-to-b inline-flex items-center justify-center rounded
             ${getCurrentMatchesGradient(match.timeStamp, match.matchState)}
            `}
              >
                <span className="absolute font-antonio font-bold text-white text-3xl transform -translate-y-0.5">
                  {game.awaySets}
                </span>
              </div>
            </div>
            <div className="flex justify-between">
              <div>
                <div className=" text-sm text-text-dark">
                  {game.awayPlayer ? (
                    game.awayPlayer.firstName
                  ) : (
                    <div className="flex gap-2 text-xl font-bold">
                      {game.awayDouble?.leaguePlayerOne?.lastName}
                      <div className="text-gray-text transform translate-y-px font-normal">
                        {getGameLinupNumber(
                          match,
                          game.awayDouble?.leaguePlayerOne?.id,
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <div className=" text-xl font-bold text-text-dark">
                    {game.awayPlayer ? (
                      game.awayPlayer.lastName
                    ) : (
                      <div className="flex gap-2">
                        {game.awayDouble?.leaguePlayerTwo?.lastName}
                        <div className="text-gray-text transform translate-y-px">
                          {getGameLinupNumber(
                            match,
                            game.awayDouble?.leaguePlayerTwo?.id,
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {game.awayPlayer && (
                    <div className="text-gray-text transform translate-y-px">
                      {getGameLinupNumber(match, game.awayPlayer.id)}
                    </div>
                  )}
                </div>
              </div>
              <div className="content-center md:mr-4">
                {currentServer === "Away" && (
                  <Image
                    height={500}
                    width={500}
                    className=" aspect-square w-8"
                    src="/assets/placeholder/Racket.png"
                    alt="Aufschlag Gast"
                  />
                )}
              </div>
            </div>
            <div
              className={`w-full h-0.5 my-2 bg-gradient-to-l ${getCurrentMatchesGradient(
                match.timeStamp,
                match.matchState,
              )}`}
            />
            <div className="flex space-x-2 items-center justify-center w-full pt-2">
              {Sets.map((i) => (
                <div
                  key={i}
                  className={`
                rounded-full border border-gray-dark w-4 h-4
                ${
                  winnerByScore(game, i) === "Away"
                    ? match.matchState === "Active"
                      ? "bg-orange"
                      : "bg-black"
                    : ""
                }`}
                />
              ))}
            </div>
          </div>
        </div>
        <div className="flex w-full justify-evenly py-4">
          {Sets.map((i) => (
            <div key={i} className="flex flex-col">
              <div className="text-center font-antonio text-lg text-gray-dark mb-1">
                {i}
              </div>
              <div className="flex flex-col border-2 rounded border-solid border-gray p-4">
                <div className="text-center font-antonio text-2xl min-w-[2ch] lg:min-w-[3ch]">
                  {scoreOrDash(game, i, "Home")}
                </div>
                <div className="h-px w-full bg-gray-light my-2" />
                <div className="text-center font-antonio text-2xl min-w-[2ch] lg:min-w-[3ch]">
                  {scoreOrDash(game, i, "Away")}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex justify-between p-4 text-gray-text">
          <div className="uppercase">{t("Game_Stats")}</div>
          <FiBarChart2 />
        </div>
        <div className="px-8 pb-4">
          {/* TODO: Reenable when server gets calculated */}
          {/* <StatCard
              homeValue={game.homePointsOfServe}
              awayValue={game.awayPointsOfServe}
              label={t("Points_On_Serve")}
            />
            <StatCard
              homeValue={game.homePointsOfReturn}
              awayValue={game.awayPointsOfReturn}
              label={t("Points_On_Return")}
            /> */}
          <StatCard
            homeValue={
              (game.set1HomeScore ?? 0) +
              (game.set2HomeScore ?? 0) +
              (game.set3HomeScore ?? 0) +
              (game.set4HomeScore ?? 0) +
              (game.set5HomeScore ?? 0)
            }
            awayValue={
              (game.set1AwayScore ?? 0) +
              (game.set2AwayScore ?? 0) +
              (game.set3AwayScore ?? 0) +
              (game.set4AwayScore ?? 0) +
              (game.set5AwayScore ?? 0)
            }
            label={t("Total_Points")}
          />
          <br />
          <StatCard
            homeValue={game.homePointsInARow}
            awayValue={game.awayPointsInARow}
            label={t("Points_In_A_Row")}
          />
          <StatCard
            homeValue={game.homeHighestLead}
            awayValue={game.awayHighestLead}
            label={t("Highest_Lead")}
          />
          <StatCard
            homeValue={game.homeLuckyPoints}
            awayValue={game.awayLuckyPoints}
            label="Lucky Points"
          />
          <StatCard
            homeValue={game.homeMatchPoints}
            awayValue={game.awayMatchPoints}
            label={t("Match_Points")}
          />
          <br />
          <StatCard
            homeValue={game.homeTimeoutUsed == 0}
            awayValue={game.awayTimeoutUsed == 0}
            label="Time Out"
          />
          <CardStatCard
            homeCards={game.cards.filter((c) =>
              scoringSides.homeIds.has(c.playerId),
            )}
            awayCards={game.cards.filter((c) =>
              scoringSides.awayIds.has(c.playerId),
            )}
          />
        </div>
      </div>
      {/* </ErrorBoundary> */}
    </>
  );
}

interface CardStatCardProps {
  homeCards: Card[];
  awayCards: Card[];
}

function CardStatCard({ homeCards, awayCards }: CardStatCardProps) {
  const { t } = useTranslation();
  return (
    <div className="flex py-2 gap-2">
      <div className="p-2 border-[1px] rounded-md border-gray-light flex gap-2 items-center">
        {homeCards.length > 0 ? (
          homeCards.map((c) => <Card card={c} key={c.id} />)
        ) : (
          <FiMinus />
        )}
      </div>
      <div className="w-full p-2 bg-bg-gray rounded-md text-center self-center ">
        {t("Cards")}
      </div>
      <div className="p-2 border-[1px] rounded-md border-gray-light flex gap-2 items-center">
        {awayCards.length > 0 ? (
          awayCards.map((c) => <Card card={c} key={c.id} />)
        ) : (
          <FiMinus />
        )}
      </div>
    </div>
  );
}

interface CardProps {
  card: Card;
}
function Card({ card }: CardProps) {
  return card.type === "YellowRed" || card.type === "YellowRed2" ? (
    <div className="rounded-sm w-4 h-6 bg-yellow ">
      <div className="ml-1 w-4 h-6 bg-red rounded-sm" />
    </div>
  ) : (
    <div
      className={`w-4 h-6 rounded-sm ${
        card.type === "Yellow" ? "bg-yellow" : "bg-red"
      }`}
    />
  );
}
interface StatCardProps {
  homeValue?: string | number | boolean | null;
  label: string;
  awayValue?: string | number | boolean | null;
}
function StatCard({ homeValue, label, awayValue }: StatCardProps) {
  return (
    <div className="flex py-2 gap-2">
      <div className="flex p-2 border-[1px] rounded-md border-gray-light">
        {typeof homeValue === "boolean" ? (
          <div
            className={`w-4 h-4 self-center rounded-full ${
              homeValue === false ? " bg-black " : "border-black border-[1px]"
            }`}
          />
        ) : (
          homeValue
        )}
      </div>
      <div className="p-2 w-full bg-bg-gray rounded-md text-center self-center">
        {label}
      </div>
      <div className="flex p-2 border-[1px] rounded-md border-gray-light">
        {typeof awayValue === "boolean" ? (
          <div
            className={`w-4 h-4 self-center rounded-full ${
              awayValue === false ? " bg-black " : "border-black border-[1px]"
            }`}
          />
        ) : (
          awayValue
        )}
      </div>
    </div>
  );
}

interface LineupProps {
  match: DetailedMatch;
}

export function Lineup({ match }: LineupProps) {
  return (
    <div className="bg-white p-4 ">
      <div className="flex items-center">
        <div className="flex items-center justify-center flex-1 p-4">
          <TeamImage team={match.homeTeam} size="lg" />
        </div>
        <div className="flex items-center justify-center flex-1 p-4">
          <TeamImage team={match.awayTeam} size="lg" />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4 last:mb-0">
        <LineupPlayer player={match.homePlayerOne} index={1} />
        <LineupPlayer player={match.guestPlayerOne} index={1} />
      </div>
      <div className="grid grid-cols-2 gap-4 mb-4 last:mb-0">
        <LineupPlayer player={match.homePlayerTwo} index={2} />
        <LineupPlayer player={match.guestPlayerTwo} index={2} />
      </div>
      <div className="grid grid-cols-2 gap-4 mb-4 last:mb-0">
        <LineupPlayer player={match.homePlayerThree} index={3} />
        <LineupPlayer player={match.guestPlayerThree} index={3} />
      </div>
      <div className="grid grid-cols-2 gap-4 mb-4 last:mb-0">
        {match.homeSubstitutePlayerOne ? (
          <LineupPlayer player={match.homeSubstitutePlayerOne} index={4} />
        ) : (
          <div />
        )}
        {match.guestSubstitutePlayerOne && (
          <LineupPlayer player={match.guestSubstitutePlayerOne} index={4} />
        )}
      </div>
      <div className="grid grid-cols-2 gap-4 mb-4 last:mb-0">
        {match.homeSubstitutePlayerTwo ? (
          <LineupPlayer player={match.homeSubstitutePlayerTwo} index={5} />
        ) : (
          <div />
        )}
        {match.guestSubstitutePlayerTwo && (
          <LineupPlayer player={match.guestSubstitutePlayerTwo} index={5} />
        )}
      </div>
      <div className="grid grid-cols-2 gap-4 mb-4 last:mb-0">
        {match.homeSubstitutePlayerThree ? (
          <LineupPlayer player={match.homeSubstitutePlayerThree} index={6} />
        ) : (
          <div />
        )}
        {match.guestSubstitutePlayerThree && (
          <LineupPlayer player={match.guestSubstitutePlayerThree} index={6} />
        )}
      </div>
    </div>
  );
}

const LineupPlayer = ({
  player,
  index,
}: {
  player: BasicLeaguePlayer | null;
  index: number;
}) => {
  return (
    <div className="inline-flex items-center space-x-2 border border-gray-light rounded p-2">
      <div className="text-gray-text">{index}</div>
      <PlayerImage player={player} size="sm" />
      <div className=" font-bold text-sm text-text-dark">
        {player?.lastName}{" "}
      </div>
    </div>
  );
};

interface MatchVenueProps {
  match: DetailedMatch;
}
function MatchVenue({ match }: MatchVenueProps) {
  const { locale } = useRouter();
  const { t } = useTranslation();
  const timeSuffix = t("Time_Suffix");
  return (
    <div className="flex flex-col space-y-4 text-lg bg-white lg:bg-bg-gray p-4 lg:rounded-lg">
      <div className="flex space-x-4 items-center">
        <FiCalendar size={24} />
        <span>
          <div>{getDayOfWeek(match.timeStamp, locale)}</div>
          <div>{getDate(match.timeStamp, locale)}</div>
        </span>
      </div>
      <div className="flex space-x-4 items-center">
        <FiClock size={24} />
        <span>
          {getMatchTimeLabel(match, locale)}
          {!match.isTimeToBeDefined && timeSuffix ? ` ${timeSuffix}` : ""}
        </span>
      </div>
      <div className="flex space-x-4 items-center">
        <FiMapPin size={24} />
        <span className="flex-1">
          <div>{match.venue.name}</div>
          <div>{match.venue.adress}</div>
          <div>
            {match.venue.zipCode} {match.venue.place}
          </div>
        </span>
        {/* <FiMap size={24} /> */}
      </div>
      {match.spectators && (
        <div className="flex space-x-4 items-center">
          <FiUser size={24} />
          <span>{match.spectators}</span>
        </div>
      )}
      <VenueImage venue={match.venue} />
    </div>
  );
}
